import { orderNumber } from "@/lib/orderNumber";

// order.customer_name/phone/address/notes are free text from checkout,
// written into this label's HTML via document.write() — which runs in the
// admin's own browser session (same origin, full access to localStorage,
// including the Supabase auth token). Without escaping, a malicious
// customer could inject a script into their order that runs in the admin's
// session the moment they print that order's label.
function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function printOrderLabel(order) {
  const ordNum  = orderNumber(order);
  const date    = order.createdAt?.seconds
    ? new Date(order.createdAt.seconds * 1000).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
      })
    : new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  const address = escapeHtml([
    order.address,
    [order.city, order.state, order.pincode].filter(Boolean).join(", "),
    order.country,
  ].filter(Boolean).join("\n"));

  const itemRows = (order.items || []).map((it, i) =>
    `<tr>
      <td style="padding:4px 6px;border-bottom:1px solid #e8d5b0;color:#555;">${i + 1}.</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e8d5b0;font-weight:600;">${escapeHtml(it.name)}</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e8d5b0;color:#666;">${escapeHtml(it.size)}</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e8d5b0;text-align:center;">× ${escapeHtml(it.quantity)}</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e8d5b0;text-align:right;font-weight:600;">₹${(it.price * it.quantity).toLocaleString("en-IN")}</td>
    </tr>`
  ).join("");

  const breakdownRows = [
    order.subtotal != null
      ? `<tr><td style="padding:2px 0;color:#666;">Items subtotal</td><td style="text-align:right;">₹${(order.subtotal).toLocaleString("en-IN")}</td></tr>`
      : "",
    order.gst_amount != null
      ? `<tr><td style="padding:2px 0;color:#666;">GST (${order.gst_percent ?? 5}%)</td><td style="text-align:right;">₹${order.gst_amount.toLocaleString("en-IN")}</td></tr>`
      : "",
    order.delivery_charge != null
      ? `<tr><td style="padding:2px 0;color:#666;">Delivery</td><td style="text-align:right;">${order.delivery_charge === 0 ? "Free" : "₹" + order.delivery_charge.toLocaleString("en-IN")}</td></tr>`
      : "",
  ].join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Order Label ${ordNum}</title>
  <style>
    @page { size: A5; margin: 10mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 12px;
      color: #1a0a04;
      background: #fff;
      width: 148mm;
    }
    .label {
      border: 2px solid #5A2D1D;
      border-radius: 4px;
      overflow: hidden;
    }

    /* Header */
    .header {
      background: #5A2D1D;
      color: #F5E6CC;
      padding: 10px 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .brand-name {
      font-size: 17px;
      font-weight: 800;
      letter-spacing: 0.5px;
    }
    .brand-sub {
      font-size: 9px;
      opacity: 0.75;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-top: 2px;
    }
    .order-num {
      text-align: right;
    }
    .order-num .label-small {
      font-size: 8px;
      opacity: 0.7;
      text-transform: uppercase;
      letter-spacing: 1.5px;
    }
    .order-num .order-id {
      font-size: 16px;
      font-weight: 800;
      font-family: monospace;
      letter-spacing: 2px;
    }

    /* Sections */
    .section {
      padding: 10px 14px;
      border-bottom: 1px solid #e0cba8;
    }
    .section:last-child { border-bottom: none; }
    .section-title {
      font-size: 8px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 2px;
      color: #556B2F;
      margin-bottom: 5px;
    }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }

    /* Ship To */
    .customer-name {
      font-size: 14px;
      font-weight: 800;
      color: #5A2D1D;
      margin-bottom: 3px;
    }
    .customer-phone {
      font-size: 11px;
      font-weight: 600;
      color: #333;
      margin-bottom: 4px;
    }
    .address {
      font-size: 11px;
      color: #333;
      white-space: pre-line;
      line-height: 1.5;
    }

    /* Meta */
    .meta-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 3px;
    }
    .meta-label { color: #666; }
    .meta-value { font-weight: 600; }

    /* Items table */
    table { width: 100%; border-collapse: collapse; }
    th {
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #666;
      padding: 4px 6px;
      border-bottom: 2px solid #5A2D1D;
      text-align: left;
    }
    th:last-child, td:last-child { text-align: right; }

    /* Totals */
    .totals-table { width: 100%; }
    .grand-total td {
      font-size: 14px;
      font-weight: 800;
      color: #5A2D1D;
      border-top: 2px solid #5A2D1D;
      padding-top: 4px;
    }

    /* Warnings */
    .warnings {
      background: #FFF8E7;
      padding: 8px 14px;
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }
    .warn-chip {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      font-weight: 700;
      color: #7A3B25;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Footer */
    .footer {
      background: #F5E6CC;
      padding: 6px 14px;
      font-size: 9px;
      color: #666;
      text-align: center;
      letter-spacing: 0.5px;
    }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="label">

    <!-- Header -->
    <div class="header">
      <div>
        <div class="brand-name">🫙 Annapurna Home Pickles</div>
        <div class="brand-sub">Handcrafted Andhra Pickles</div>
      </div>
      <div class="order-num">
        <div class="label-small">Order</div>
        <div class="order-id">${ordNum}</div>
        <div class="label-small">${date}</div>
      </div>
    </div>

    <!-- Ship To + Order Meta -->
    <div class="section two-col">
      <div>
        <div class="section-title">Ship To</div>
        <div class="customer-name">${escapeHtml(order.customer_name) || "—"}</div>
        <div class="customer-phone">${escapeHtml(order.phone)}</div>
        <div class="address">${address}</div>
      </div>
      <div>
        <div class="section-title">Order Info</div>
        ${order.notes ? `<div style="font-size:10px;color:#555;font-style:italic;">Note: ${escapeHtml(order.notes)}</div>` : ""}
      </div>
    </div>

    <!-- Items -->
    <div class="section">
      <div class="section-title">Items</div>
      <table>
        <thead>
          <tr>
            <th style="width:20px;">#</th>
            <th>Product</th>
            <th>Size</th>
            <th style="text-align:center;">Qty</th>
            <th style="text-align:right;">Amount</th>
          </tr>
        </thead>
        <tbody>${itemRows}</tbody>
      </table>
    </div>

    <!-- Price Summary -->
    <div class="section">
      <div class="section-title">Payment Summary</div>
      <table class="totals-table">
        <tbody>
          ${breakdownRows}
          <tr class="grand-total">
            <td>Grand Total</td>
            <td>₹${(order.total || 0).toLocaleString("en-IN")}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Warnings -->
    <div class="warnings">
      <div class="warn-chip">🌡️ Keep Cool & Dry</div>
      <div class="warn-chip">🍽️ Food Product</div>
      <div class="warn-chip">📦 Do Not Crush</div>
    </div>

    <!-- Footer -->
    <div class="footer">
      Annapurna Home Pickles · ahpickles.in · Handcrafted with love in Andhra Pradesh
    </div>

  </div>

  <script>
    window.onload = function () { window.print(); };
  </script>
</body>
</html>`;

  const win = window.open("", "_blank", "width=620,height=800");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
